/**
 * ReadMeHelp.js
 *
 * The Cost Breakdown module ReadMe: how the portal-native estimation flow
 * works (Cost Breakdown v2 — one estimate spine on core.estimates, libraries,
 * roll-ups, live one-pager). Replaces the old Excel-workbook colour legend.
 */

import React from 'react';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';

const ACCENT = '#0f766e';

const FLOW = [
  {
    icon: '📕', title: '1 · Estimate Log', color: '#b91c1c',
    text: 'One registry for the whole portal (the same estimates as the header dropdown, MTO, and award-to-job). Create the estimate here, pick its disciplines, and click it to make it active. Track the bid with statuses: Draft → In Progress → Submitted → Awarded / Lost.',
  },
  {
    icon: '🔍', title: '2 · Detailed Estimation (per discipline)', color: '#2563eb',
    text: 'The scope-item takeoff: one row per tag / equipment / assembly with hours, materials, rentals, and subs. One sheet design serves all 12 disciplines — column groups adapt per discipline, and totals (⚡ columns) calculate themselves.',
  },
  {
    icon: '📊', title: '3 · Discipline Summary (per discipline)', color: '#7c3aed',
    text: 'The Labour Planning Worksheet: 13 sections from Project Support Staff to Site Expense. "Roll up to Summary" pushes the detail sheet totals here (AUTO rows); the rest is manual or imported from the library.',
  },
  {
    icon: '👥', title: '4 · Indirects & Site Expenses (per estimate)', color: '#6366f1',
    text: 'Estimate-wide sheets: indirect staff and supervision across pre-site / site / post-site phases (plus vehicles), and the recurring site facility costs. Import the standard rows from the libraries, then tailor.',
  },
  {
    icon: '📄', title: '5 · CB Summary one-pager', color: ACCENT,
    text: 'Computed LIVE from everything above — cost types × disciplines, plus indirects and site expenses, with markup per cost type on top. Nothing is stored, so it can never drift. Click a discipline column to drill down.',
  },
];

const LIBRARIES = [
  { icon: '💵', name: 'Labour Rates Bank', text: 'Blended day/night-shift rates per trade and section — the reference for all rate lookups.' },
  { icon: '🚜', name: 'Rentals', text: 'Equipment rental rates (hourly / daily / weekly / monthly).' },
  { icon: '👥', name: 'Indirects Library', text: 'Standard indirect positions to import into an estimate.' },
  { icon: '🏗️', name: 'Site Expenses Library', text: 'The standard site-expense checklist.' },
  { icon: '👷', name: 'Crew Calc Library', text: 'Typical crew-size scenarios per discipline.' },
  { icon: '📝', name: 'Clarifications Library', text: 'LGL standard assumptions, inclusions, and exclusions.' },
];

const ReadMeHelp = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div style={{ backgroundColor: 'white', borderRadius: 16, width: 780, maxWidth: '94%', maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '18px 28px', background: ACCENT, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.3rem' }}>📖 Cost Breakdown — ReadMe</h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', opacity: 0.85 }}>How estimating works in the portal</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} data-audio-skip>
            <ListenButton compact dark guideId="cb-readme-help" title="Cost Breakdown ReadMe" accent={ACCENT} />
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#fff' }}>×</button>
          </div>
        </div>

        <div style={{ overflowY: 'auto', padding: '20px 28px 28px', flex: 1, fontSize: 14, lineHeight: 1.65, color: '#374151' }}>
          <HelpWithVideoTour guideId="cb-readme-help" title="Cost Breakdown ReadMe" accent={ACCENT}>
          <p style={{ marginTop: 0 }}>
            The Cost Breakdown is fully <strong>portal-native</strong> — there is no Excel workbook to import.
            Everything hangs off <strong>one selected estimate</strong> (top-bar dropdown or the Estimate Log),
            and numbers flow <strong>upward automatically</strong>: detail → discipline summary → one-pager.
          </p>

          <h3 style={{ margin: '18px 0 10px', color: '#1f2937' }}>The flow</h3>
          {FLOW.map((s) => (
            <div key={s.title} style={{ display: 'flex', gap: 14, padding: '10px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'flex-start' }}>
              <div style={{ fontSize: 26, flexShrink: 0 }}>{s.icon}</div>
              <div>
                <div style={{ fontWeight: 700, color: s.color, marginBottom: 2 }}>{s.title}</div>
                <div style={{ fontSize: 13.5 }}>{s.text}</div>
              </div>
            </div>
          ))}

          <h3 style={{ margin: '22px 0 10px', color: '#1f2937' }}>Libraries vs estimate data</h3>
          <p style={{ fontSize: 13.5 }}>
            Bank pages are <strong>shared reference libraries</strong>. Each estimate <strong>imports a filtered copy</strong> of the rows it needs
            (📚 Import from Library) and then tailors them freely — edits inside an estimate never touch the library, and vice-versa.
            With <strong>no estimate selected</strong>, the same pages switch to library mode so you can maintain the reference data itself.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
            {LIBRARIES.map((l) => (
              <div key={l.name} style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{l.icon} {l.name}</div>
                <div style={{ fontSize: 12.5, color: '#6b7280' }}>{l.text}</div>
              </div>
            ))}
          </div>

          <h3 style={{ margin: '22px 0 10px', color: '#1f2937' }}>Conventions</h3>
          <ul style={{ paddingLeft: 20, fontSize: 13.5 }}>
            <li><strong>Click</strong> any cell to edit — Enter saves, Escape cancels.</li>
            <li>Columns marked <strong style={{ color: '#7c3aed' }}>⚡</strong> are calculated and read-only.</li>
            <li>Rows with account <strong>AUTO</strong> were rolled up from Detailed Estimation — re-rolling refreshes them in place.</li>
            <li>Every sheet has 📥 <strong>Export</strong> (Excel) and a ❓ <strong>Help</strong> with a 🎧 audio guide.</li>
            <li>Awarding an estimate (Admin → Estimate Management) creates the project and pushes the MTO into its tracker.</li>
          </ul>
          </HelpWithVideoTour>
        </div>
      </div>
    </div>
  );
};

export default ReadMeHelp;
